import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreatePrepaymentInput,
  Prepayment,
  UpdatePrepaymentInput,
} from "../../lib/realestate";
import { Modal } from "../ui/Modal";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreatePrepaymentInput | UpdatePrepaymentInput) => Promise<void>;
  existing: Prepayment | null;
  submitting: boolean;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function centsToEuroDe(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

export function PrepaymentFormModal({ open, onClose, onSubmit, existing, submitting }: Props) {
  const { t } = useTranslation();
  const [date, setDate] = useState(existing?.date ?? todayIso());
  const [amount, setAmount] = useState(existing ? centsToEuroDe(existing.amountCents) : "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDate(existing?.date ?? todayIso());
    setAmount(existing ? centsToEuroDe(existing.amountCents) : "");
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing]);

  const title = existing
    ? t("realestate.prepayment.form_title_edit")
    : t("realestate.prepayment.add_title");

  async function handleSubmit() {
    setError(null);
    try {
      const amountCents = parseEuroDe(amount);
      const trimmedNotes = notes.trim();
      await onSubmit({
        date,
        amountCents,
        notes: trimmedNotes === "" ? null : trimmedNotes,
      });
      if (!existing) {
        setAmount("");
        setNotes("");
      }
      onClose();
    } catch {
      setError(t("realestate.errors.invalid_amount"));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("realestate.prepayment.date")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.prepayment.amount")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="5.000,00"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.prepayment.notes")}</span>
          <textarea
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={2}
          />
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
